import { Injectable } from '@angular/core';

/**
 * Audio playback configuration
 */
export interface AudioConfig {
  volume?: number;
  loop?: boolean;
  playbackRate?: number;
}

/**
 * SharedAudioService
 * 
 * Centralized audio management for all components and games.
 * Handles browser autoplay policies and provides volume control.
 * 
 * @example
 * ```typescript
 * constructor(private audioService: AudioService) {}
 * 
 * onMatch() {
 *   this.audioService.play('/assets/sfx/match.mp3', { volume: 0.5 });
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AudioService {
  private isMuted = false;
  private isEnabled = true; // Campaign-level setting
  private globalVolume = 1.0;
  private audioCache = new Map<string, HTMLAudioElement>();

  /**
   * Set whether audio is enabled (campaign-level setting)
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Check if audio is enabled
   */
  isEnabledState(): boolean {
    return this.isEnabled;
  }

  /**
   * Play audio file with optional configuration
   * @param src Audio file path
   * @param config Optional audio configuration
   * @returns Promise that resolves when playback starts
   */
  async play(src: string, config: Partial<AudioConfig> = {}): Promise<void> {
    if (!this.isEnabled || this.isMuted) {
      return;
    }

    try {
      // Check if audio is cached
      let audio = this.audioCache.get(src);
      
      if (!audio) {
        audio = new Audio(src);
        this.audioCache.set(src, audio);
      }

      // Reset to start if already playing
      audio.currentTime = 0;

      // Apply configuration
      audio.volume = (config.volume ?? 1.0) * this.globalVolume;
      audio.loop = config.loop ?? false;
      audio.playbackRate = config.playbackRate ?? 1.0;

      // Attempt playback
      await audio.play();
    } catch (error) {
      // Handle autoplay policy restrictions
      console.warn('[AudioService] Playback failed (likely autoplay policy):', src, error);
    }
  }

  /**
   * Set global volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.globalVolume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Get current global volume
   */
  getVolume(): number {
    return this.globalVolume;
  }

  /**
   * Mute all audio
   */
  mute(): void {
    this.isMuted = true;
  }

  /**
   * Unmute all audio
   */
  unmute(): void {
    this.isMuted = false;
  }

  /**
   * Toggle mute state
   */
  toggleMute(): void {
    this.isMuted = !this.isMuted;
  }

  /**
   * Check if currently muted
   */
  isMutedState(): boolean {
    return this.isMuted;
  }

  /**
   * Preload audio file into cache
   */
  preload(src: string): void {
    if (!this.audioCache.has(src)) {
      const audio = new Audio(src);
      this.audioCache.set(src, audio);
    }
  }

  /**
   * Clear audio cache
   */
  clearCache(): void {
    this.audioCache.clear();
  }
}
